import { Box, Flex, Text } from '@chakra-ui/react';
import { formatDate } from 'src/utils/date';
import TextWithIcon from './TextWithIcon';
import {
  faBookSparkles,
  faCalendarDay,
  faFileDownload,
  faFileLines,
  faHashtag
} from '@fortawesome/pro-regular-svg-icons';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useQuery } from '@tanstack/react-query';
import { fetchSubmissionContext } from 'src/api/queries';
import RichTextPreview from './Submission/GenericFieldComponents/RichText/RichTextPreview';
import { ButtonText, Link } from '@morressier/alembic';
import { openFile } from 'src/utils/file';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAssessmentData } from 'src/contexts/AssessmentDataProvider';
import { useAdjudicationData } from 'src/contexts/AdjudicationDataProvider';
import { useReviewData } from 'src/contexts/ReviewDataProvider';
import AuthorsTitle from './Submission/AuthorsTitle';
import { useLocation } from 'react-router-dom';
type IProps = {
  headingText?: string;
  showManuscript?: boolean;
  manuscriptLinkText?: string;
  entryPoint?: 'early-assessment' | 'adjudication' | 'coReview';
};

export const SubmissionInfoCard = (props: IProps) => {
  const location = useLocation();
  const path = location.pathname;
  const isValidPath =
    path.includes('/my-submissions/') || path.includes('/workspace/');
  const {
    workflowResponse: { workflowData, submissionContextId, manuscriptId },
    fieldsResponse
  } = ((): ReturnType<
    | typeof useAssessmentData
    | typeof useAdjudicationData
    | typeof useReviewData
    | typeof useSubmissionData
  > => {
    switch (props.entryPoint) {
      case 'early-assessment':
        return useAssessmentData();
      case 'adjudication':
        return useAdjudicationData();
      case 'coReview':
        return useReviewData();
      default:
        return useSubmissionData();
    }
  })();

  const submissionType = fieldsResponse.articleTypeName;

  const manuscriptUrl = (
    fieldsResponse.fields.Manuscript?.value as ISubmissionFile
  )?.url;

  const isDoubleAnonymous =
    fieldsResponse.fields['DoubleAnonymousPeerReview']?.value;

  const anonymizedManuscriptUrl = (
    fieldsResponse.fields.AnonymizedManuscript?.value as ISubmissionFile
  )?.url;

  const title = fieldsResponse.fields.Title!.value as string;
  const submissionDate = workflowData.states?.find(state =>
    ['initial', 'transferOffered'].includes(state.name)
  )?.completedWith?.date;

  const { data: journal } = useQuery({
    queryKey: ['submission-context', submissionContextId],
    queryFn: () => fetchSubmissionContext(submissionContextId),
    enabled: !!submissionContextId
  });

  const selectedFocusIssue = journal?.data?.focusIssues?.find(
    item => item.id === fieldsResponse?.fields?.FocusIssue?.value
  );
  return (
    <Flex flexDirection="column" gap={2}>
      {props.headingText && (
        <Text textStyle="headingOverline" textTransform="uppercase">
          {props.headingText}
        </Text>
      )}
      <Box textStyle="headingItem">
        <RichTextPreview value={title} style={{ fontSize: '1.25rem' }} />
        {isValidPath && <AuthorsTitle />}
      </Box>

      <Flex gap={4} flexWrap="wrap">
        <TextWithIcon
          icon={faCalendarDay}
          text={formatDate(new Date(submissionDate ?? ''))}
        />
        <TextWithIcon icon={faFileLines} text={submissionType} />
        <TextWithIcon icon={faHashtag} text={manuscriptId} />
      </Flex>
      {selectedFocusIssue && (
        <Flex flexDirection="row" alignContent="center" gap={2}>
          <TextWithIcon icon={faBookSparkles} text={'Special Issue: '} />
          {selectedFocusIssue?.issueLink || journal?.data?.guidelineLink ? (
            <Link
              variant="primary"
              href={
                selectedFocusIssue?.issueLink || journal?.data?.guidelineLink
              }
              target="_blank"
            >
              {selectedFocusIssue.name}
            </Link>
          ) : (
            <Text textStyle="textContext">{selectedFocusIssue.name}</Text>
          )}
        </Flex>
      )}
      {props.showManuscript && (
        <Flex flexDirection={'row'} gap={8}>
          <ButtonText
            onClick={() => {
              openFile(
                isDoubleAnonymous ? anonymizedManuscriptUrl : manuscriptUrl
              );
            }}
            iconLeft={<FontAwesomeIcon icon={faFileDownload} />}
            marginLeft={-1}
          >
            {props.manuscriptLinkText ?? 'View Manuscript'}
          </ButtonText>
        </Flex>
      )}
    </Flex>
  );
};
